"use client";

import React from "react";
import { Star, Info } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
function ProStaffPicksConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="223237" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-[450px] rounded-xl bg-muted/40 animate-pulse border border-border/50" />)}
      </div>;
  }
  if (error || products.length === 0) {
    return <div className="text-center py-16 px-4 border border-dashed border-border rounded-sm bg-muted/10">
        <p className="text-lg text-muted-foreground font-default">Trenutno nema istaknutih dijelova u ponudi. Molimo provjerite ponovno uskoro.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {products.map((product, i) => <div key={product.id} data-index={i} className="h-full">
          <Card className="h-full flex flex-col overflow-hidden border-border shadow-sm hover:shadow-lg transition-shadow duration-300 bg-card rounded-sm">
            <Link to={product.link} className="block aspect-square overflow-hidden bg-muted relative group">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-secondary/10">
                  <span className="font-default text-sm">Nema slike</span>
                </div>}
              <div className="absolute top-3 left-3 bg-primary text-primary-foreground backdrop-blur-sm px-2.5 py-1 rounded-sm text-xs font-bold font-default border border-primary/20 shadow-xs uppercase tracking-wider">
                Premium
              </div>
            </Link>

            <CardHeader className="p-5 pb-3 flex-none space-y-3">
              <div className="flex justify-between items-start gap-3">
                <Link to={product.link} className="hover:underline group-hover:text-primary transition-colors">
                  <CardTitle className="text-lg font-default leading-tight line-clamp-2 text-card-foreground">
                    {product.title}
                  </CardTitle>
                </Link>
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-primary hover:bg-accent rounded-full">
                      <Info className="h-4 w-4" />
                      <span className="sr-only">Tehničke specifikacije</span>
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80 border-border shadow-xl bg-popover text-popover-foreground rounded-sm">
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold font-default flex items-center gap-2 border-b border-border pb-2">
                        <Info className="h-4 w-4 text-primary" />
                        Tehničke Specifikacije
                      </h4>
                      {product.short_description ? <div className="text-sm text-muted-foreground prose prose-sm dark:prose-invert max-w-none [&_p]:mb-2 last:[&_p]:mb-0 font-default leading-relaxed" dangerouslySetInnerHTML={{
                    __html: product.short_description
                  }} /> : <p className="text-sm text-muted-foreground font-default">Detaljne specifikacije trenutno nisu dostupne za ovaj proizvod.</p>}
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, j) => <Star key={j} data-index={j} className="h-4 w-4 fill-primary text-primary" />)}
                <span className="text-xs text-muted-foreground ml-2 font-default font-medium">(4.9)</span>
              </div>
            </CardHeader>

            <CardContent className="p-5 pt-0 flex-grow flex flex-col justify-end">
              <div className="text-2xl font-bold font-mono text-foreground">
                {product.price?.toFixed(2) || "0.00"} €
              </div>
            </CardContent>

            <CardFooter className="p-5 pt-0 flex flex-col gap-3">
              <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold shadow-sm rounded-sm transition-colors" text="Dodaj u košaricu" showQuantity={false} />
              <Button variant="outline" className="w-full border-border hover:bg-accent hover:text-accent-foreground font-medium shadow-xs rounded-sm" asChild>
                <Link to={product.link}>Detalji proizvoda</Link>
              </Button>
            </CardFooter>
          </Card>
        </div>)}
    </div>;
}
export default function HartleysProStaffPicks() {
  const wp_query_params = {
    per_page: 4,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="izdvojeni-dijelovi-iz-trgovine" className="py-16 md:py-24 bg-background relative overflow-hidden text-foreground">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(var(--color-primary) 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }}></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-12 md:mb-16 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-4 tracking-tight uppercase">
            Izdvojeni Dijelovi
          </h2>
          <div className="h-1 w-20 bg-primary mx-auto mb-6 rounded-none"></div>
          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            Vrhunski rezervni dijelovi i oprema za električne romobile. Odabrali naši stručnjaci iz ChargeZone servisa za maksimalnu pouzdanost i performanse vašeg vozila.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 4,
        order: "desc",
        orderby: "date"
      }}>
          <ProStaffPicksConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}